import React, { useRef } from 'react'
import { Bold, Italic, List, ListOrdered } from 'lucide-react'

interface RichTextEditorProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  required?: boolean
  minHeight?: string
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  required,
  minHeight = '80px'
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const insertFormatting = (prefix: string, suffix: string = '') => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = value.substring(start, end)
    
    const newText = 
      value.substring(0, start) + 
      prefix + selectedText + suffix +
      value.substring(end)
    
    onChange(newText)
    
    // Reset cursor position
    setTimeout(() => {
      textarea.focus()
      const newCursorPos = start + prefix.length + selectedText.length + suffix.length
      textarea.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }

  const insertBulletPoint = () => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    const newText = 
      value.substring(0, lineStart) + 
      '• ' +
      value.substring(lineStart)
    
    onChange(newText)
    
    setTimeout(() => {
      textarea.focus()
      const newCursorPos = lineStart + 2
      textarea.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }

  const insertNumberedList = () => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    
    // Count existing numbered items
    const lines = value.substring(0, start).split('\n')
    let lastNumber = 0
    for (let i = lines.length - 1; i >= 0; i--) {
      const match = lines[i].match(/^(\d+)\.\s/)
      if (match) {
        lastNumber = parseInt(match[1])
        break
      }
    }
    
    const nextNumber = lastNumber + 1
    const newText = 
      value.substring(0, lineStart) + 
      `${nextNumber}. ` +
      value.substring(lineStart)
    
    onChange(newText)
    
    setTimeout(() => {
      textarea.focus()
      const newCursorPos = lineStart + `${nextNumber}. `.length
      textarea.setSelectionRange(newCursorPos, newCursorPos)
    }, 0)
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        display: 'flex',
        gap: '4px',
        padding: '4px',
        borderTop: '1px solid #d1d5db',
        borderLeft: '1px solid #d1d5db',
        borderRight: '1px solid #d1d5db',
        borderRadius: '6px 6px 0 0',
        background: '#f9fafb'
      }}>
        <button
          type="button"
          onClick={() => insertFormatting('**', '**')}
          title="Bold (wrap text with **)"
          style={{
            padding: '4px 8px',
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: '#374151'
          }}
        >
          <Bold style={{ height: '16px', width: '16px' }} />
        </button>
        <button
          type="button"
          onClick={() => insertFormatting('*', '*')}
          title="Italic (wrap text with *)"
          style={{
            padding: '4px 8px',
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: '#374151'
          }}
        >
          <Italic style={{ height: '16px', width: '16px' }} />
        </button>
        <button
          type="button"
          onClick={insertBulletPoint}
          title="Bullet point"
          style={{
            padding: '4px 8px',
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: '#374151'
          }}
        >
          <List style={{ height: '16px', width: '16px' }} />
        </button>
        <button
          type="button"
          onClick={insertNumberedList}
          title="Numbered list"
          style={{
            padding: '4px 8px',
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: '#374151'
          }}
        >
          <ListOrdered style={{ height: '16px', width: '16px' }} />
        </button>
        <span style={{
          marginLeft: 'auto',
          padding: '4px 8px',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          Use **text** for bold, *text* for italic
        </span>
      </div>
      <textarea
        ref={textareaRef}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #d1d5db',
          borderTop: 'none',
          borderRadius: '0 0 6px 6px',
          fontSize: '14px',
          minHeight,
          resize: 'vertical',
          fontFamily: 'monospace'
        }}
      />
    </div>
  )
}